// Simple transaction store for managing failed, RTGS, and amendment transactions
type RTGSStatus = 'SENT_TO_BANK' | 'REVERSED' | 'MANUALLY_SETTLED'

interface RTGSTransaction {
  id: string
  mtoName: string
  mtoRef: string
  bankRef: string
  branchCode: string
  agentCode: string
  payoutType: 'FT' | 'IBFT' | 'OTC'
  amountFCY: string
  currencyFCY: string
  amountPKR: string
  remitterName: string
  beneficiaryName: string
  beneficiaryIBAN: string
  rtgsStatus: RTGSStatus
  sentToRTGSAt: string
  createdAt: string
  currentStage: string
  lifecycle: string
  transactionStatus: string
  status: string
  corridor: string
  purposeCode: string
  purposeText: string
}

interface RepushedTransaction {
  id: string
  mtoName: string
  mtoRef: string
  bankRef: string
  branchCode: string
  agentCode: string
  payoutType: 'FT' | 'IBFT' | 'OTC'
  amountFCY: string
  currencyFCY: string
  amountPKR: string
  corridor: string
  purposeCode: string
  purposeText: string
  remitterName: string
  beneficiaryName: string
  beneficiaryIBAN: string
  currentState: string
  lifecycleStatus: string
  createdAt: string
  updatedAt: string
  railRefs: any
  stateHistory: any[]
}

export interface PendingAmendment {
  id: string
  transactionId: string
  mtoName: string
  payoutType: string
  amountFCY: string
  amountPKR: string
  originalAccount: string
  originalTitle: string
  newAccount: string
  fetchedTitle: string
  reason: string
  requestedBy: string
  requestedAt: string
  status: 'PENDING' | 'APPROVED' | 'REJECTED'
  checkerComment?: string
}

export interface PendingCancellation {
  id: string
  transactionId: string
  mtoName: string
  payoutType: string
  amountFCY: string
  amountPKR: string
  reason: string
  requestedBy: string
  requestedAt: string
  status: 'PENDING' | 'APPROVED' | 'REJECTED'
  checkerComment?: string
  cancellationAction: string
}

export interface PendingSettlement {
  id: string
  transactionId: string
  mtoName: string
  payoutType: string
  amountFCY: string
  amountPKR: string
  reason: string
  requestedBy: string
  requestedAt: string
  status: 'PENDING' | 'APPROVED' | 'REJECTED'
  checkerComment?: string
}

export interface ActionHistoryEntry {
  action: string
  actor: string
  timestamp: string
  reason: string
}

export interface TransactionOverride {
  amendStatus?: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED'
  cancelStatus?: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED'
  settleStatus?: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED'
  lifecycleStatus?: string
  currentState?: string
  beneficiaryIBAN?: string
  beneficiaryName?: string
  updatedAt?: string
  amendmentHistory?: any[]
  actionHistory?: ActionHistoryEntry[]
}

class TransactionStore {
  private rtgsTransactions: RTGSTransaction[] = []
  private repushedTransactions: RepushedTransaction[] = []
  private pendingAmendments: PendingAmendment[] = []
  private pendingCancellations: PendingCancellation[] = []
  private pendingSettlements: PendingSettlement[] = []
  private overrides: Record<string, TransactionOverride> = {}
  private listeners: Set<() => void> = new Set()

  private addActionHistory(
    transactionId: string,
    action: string,
    actor: string,
    reason: string,
  ) {
    if (!this.overrides[transactionId]) {
      this.overrides[transactionId] = {}
    }
    if (!this.overrides[transactionId].actionHistory) {
      this.overrides[transactionId].actionHistory = []
    }
    this.overrides[transactionId].actionHistory!.push({
      action,
      actor,
      timestamp: new Date().toISOString(),
      reason,
    })
  }

  addToRTGS(transaction: any) {
    const rtgsTransaction: RTGSTransaction = {
      id: transaction.id,
      mtoName: transaction.mtoName,
      mtoRef: transaction.mtoRef,
      bankRef: transaction.bankRef,
      branchCode: transaction.branchCode,
      agentCode: transaction.agentCode,
      payoutType: transaction.payoutType,
      amountFCY: transaction.amountFCY,
      currencyFCY: transaction.currencyFCY,
      amountPKR: transaction.amountPKR,
      remitterName: transaction.remitterName,
      beneficiaryName: transaction.beneficiaryName,
      beneficiaryIBAN: transaction.beneficiaryIBAN,
      rtgsStatus: 'SENT_TO_BANK',
      sentToRTGSAt: new Date().toISOString(),
      createdAt: transaction.createdAt,
      currentStage: 'RTGS_POST',
      lifecycle: 'IN_PROGRESS',
      transactionStatus: 'PENDING_RTGS',
      status: 'IN_PROGRESS',
      corridor: transaction.corridor,
      purposeCode: transaction.purposeCode,
      purposeText: transaction.purposeText,
    }

    this.rtgsTransactions.push(rtgsTransaction)
    this.addActionHistory(
      transaction.id,
      'Move to RTGS',
      'admin',
      'Moved to RTGS queue',
    )
    this.notifyListeners()
  }

  repushTransaction(transaction: any) {
    // Convert failed transaction back to in-progress transaction
    const repushedTransaction: RepushedTransaction = {
      id: transaction.id,
      mtoName: transaction.mtoName,
      mtoRef: transaction.mtoRef,
      bankRef: transaction.bankRef,
      branchCode: transaction.branchCode,
      agentCode: transaction.agentCode,
      payoutType: transaction.payoutType,
      amountFCY: transaction.amountFCY,
      currencyFCY: transaction.currencyFCY,
      amountPKR: transaction.amountPKR,
      corridor: transaction.corridor,
      purposeCode: transaction.purposeCode,
      purposeText: transaction.purposeText,
      remitterName: transaction.remitterName,
      beneficiaryName: transaction.beneficiaryName,
      beneficiaryIBAN: transaction.beneficiaryIBAN,
      currentState: transaction.currentState, // Resume from where it failed
      lifecycleStatus: 'IN_PROGRESS',
      createdAt: transaction.createdAt,
      updatedAt: new Date().toISOString(),
      railRefs: {},
      stateHistory: [
        ...transaction.stateHistory,
        {
          stateCode: transaction.currentState,
          status: 'PENDING',
          timestamp: new Date().toISOString(),
          primaryRef: `RETRY-${transaction.id}`,
          attempts: (transaction.attemptCount || 0) + 1,
        },
      ],
    }

    this.repushedTransactions.push(repushedTransaction)

    if (!this.overrides[transaction.id]) {
      this.overrides[transaction.id] = {}
    }
    this.overrides[transaction.id].lifecycleStatus = 'IN_PROGRESS'
    this.overrides[transaction.id].updatedAt = new Date().toISOString()

    this.addActionHistory(
      transaction.id,
      'Retry',
      'admin',
      'Transaction retried',
    )
    this.notifyListeners()
  }

  getRTGSTransactions(): RTGSTransaction[] {
    return [...this.rtgsTransactions]
  }

  getRepushedTransactions(): RepushedTransaction[] {
    return [...this.repushedTransactions]
  }

  updateRTGSStatus(transactionIds: string[], status: RTGSStatus) {
    this.rtgsTransactions = this.rtgsTransactions.map((txn) => {
      if (transactionIds.includes(txn.id)) {
        return {
          ...txn,
          rtgsStatus: status,
          lifecycle:
            status === 'MANUALLY_SETTLED'
              ? 'SUCCESS'
              : status === 'REVERSED'
                ? 'FAILED'
                : 'IN_PROGRESS',
          transactionStatus:
            status === 'MANUALLY_SETTLED'
              ? 'COMPLETED'
              : status === 'REVERSED'
                ? 'FAILED'
                : 'PENDING_RTGS',
        }
      }
      return txn
    })
    this.notifyListeners()
  }

  // --- Amendments ---

  submitAmendment(
    data: Omit<PendingAmendment, 'id' | 'requestedAt' | 'status'>,
  ) {
    const newAmendment: PendingAmendment = {
      ...data,
      id: `AMEND-${Date.now()}`,
      requestedAt: new Date().toISOString(),
      status: 'PENDING',
    }

    // Remove any existing rejected amendments for this transaction
    this.pendingAmendments = this.pendingAmendments.filter(
      (a) =>
        !(a.transactionId === data.transactionId && a.status === 'REJECTED'),
    )

    this.pendingAmendments.push(newAmendment)

    if (!this.overrides[data.transactionId]) {
      this.overrides[data.transactionId] = {}
    }
    this.overrides[data.transactionId].amendStatus = 'PENDING'
    this.overrides[data.transactionId].updatedAt = new Date().toISOString()

    this.addActionHistory(
      data.transactionId,
      'Submit Amendment',
      data.requestedBy,
      data.reason,
    )
    this.notifyListeners()
  }

  approveAmendment(amendmentId: string, checkerComment: string) {
    const amendment = this.pendingAmendments.find((a) => a.id === amendmentId)
    if (amendment) {
      amendment.status = 'APPROVED'
      amendment.checkerComment = checkerComment

      if (!this.overrides[amendment.transactionId]) {
        this.overrides[amendment.transactionId] = {}
      }

      this.overrides[amendment.transactionId].amendStatus = 'APPROVED'
      this.overrides[amendment.transactionId].beneficiaryIBAN =
        amendment.newAccount
      this.overrides[amendment.transactionId].beneficiaryName =
        amendment.fetchedTitle
      this.overrides[amendment.transactionId].updatedAt =
        new Date().toISOString()

      if (!this.overrides[amendment.transactionId].amendmentHistory) {
        this.overrides[amendment.transactionId].amendmentHistory = []
      }
      this.overrides[amendment.transactionId].amendmentHistory!.push({
        date: new Date().toISOString(),
        originalAccount: amendment.originalAccount,
        newAccount: amendment.newAccount,
        originalTitle: amendment.originalTitle,
        newTitle: amendment.fetchedTitle,
        reason: amendment.reason,
        status: 'APPROVED',
        checkerComment,
      })

      this.addActionHistory(
        amendment.transactionId,
        'Approve Amendment',
        'checker',
        checkerComment,
      )
      this.notifyListeners()
    }
  }

  rejectAmendment(amendmentId: string, checkerComment: string) {
    const amendment = this.pendingAmendments.find((a) => a.id === amendmentId)
    if (amendment) {
      amendment.status = 'REJECTED'
      amendment.checkerComment = checkerComment

      if (!this.overrides[amendment.transactionId]) {
        this.overrides[amendment.transactionId] = {}
      }
      this.overrides[amendment.transactionId].amendStatus = 'REJECTED'
      this.overrides[amendment.transactionId].updatedAt =
        new Date().toISOString()

      if (!this.overrides[amendment.transactionId].amendmentHistory) {
        this.overrides[amendment.transactionId].amendmentHistory = []
      }
      this.overrides[amendment.transactionId].amendmentHistory!.push({
        date: new Date().toISOString(),
        originalAccount: amendment.originalAccount,
        newAccount: amendment.newAccount,
        originalTitle: amendment.originalTitle,
        newTitle: amendment.fetchedTitle,
        reason: amendment.reason,
        status: 'REJECTED',
        checkerComment,
      })

      this.addActionHistory(
        amendment.transactionId,
        'Reject Amendment',
        'checker',
        checkerComment,
      )
      this.notifyListeners()
    }
  }

  getPendingAmendments(): PendingAmendment[] {
    return this.pendingAmendments.filter((a) => a.status === 'PENDING')
  }

  getAllAmendments(): PendingAmendment[] {
    return [...this.pendingAmendments]
  }

  // --- Cancellations ---

  submitCancellation(
    data: Omit<PendingCancellation, 'id' | 'requestedAt' | 'status'>,
  ) {
    const newCancellation: PendingCancellation = {
      ...data,
      id: `CANCEL-${Date.now()}`,
      requestedAt: new Date().toISOString(),
      status: 'PENDING',
    }

    this.pendingCancellations = this.pendingCancellations.filter(
      (c) =>
        !(c.transactionId === data.transactionId && c.status === 'REJECTED'),
    )

    this.pendingCancellations.push(newCancellation)

    if (!this.overrides[data.transactionId]) {
      this.overrides[data.transactionId] = {}
    }
    this.overrides[data.transactionId].cancelStatus = 'PENDING'
    this.overrides[data.transactionId].updatedAt = new Date().toISOString()

    this.addActionHistory(
      data.transactionId,
      'Submit Cancellation',
      data.requestedBy,
      data.reason,
    )
    this.notifyListeners()
  }

  approveCancellation(cancellationId: string, checkerComment: string) {
    const cancellation = this.pendingCancellations.find(
      (c) => c.id === cancellationId,
    )
    if (cancellation) {
      cancellation.status = 'APPROVED'
      cancellation.checkerComment = checkerComment

      if (!this.overrides[cancellation.transactionId]) {
        this.overrides[cancellation.transactionId] = {}
      }

      this.overrides[cancellation.transactionId].cancelStatus = 'APPROVED'
      this.overrides[cancellation.transactionId].lifecycleStatus = 'CANCELLED'
      this.overrides[cancellation.transactionId].updatedAt =
        new Date().toISOString()

      this.addActionHistory(
        cancellation.transactionId,
        'Approve Cancellation',
        'checker',
        checkerComment,
      )
      this.notifyListeners()
    }
  }

  rejectCancellation(cancellationId: string, checkerComment: string) {
    const cancellation = this.pendingCancellations.find(
      (c) => c.id === cancellationId,
    )
    if (cancellation) {
      cancellation.status = 'REJECTED'
      cancellation.checkerComment = checkerComment

      if (!this.overrides[cancellation.transactionId]) {
        this.overrides[cancellation.transactionId] = {}
      }
      this.overrides[cancellation.transactionId].cancelStatus = 'REJECTED'
      this.overrides[cancellation.transactionId].updatedAt =
        new Date().toISOString()

      this.addActionHistory(
        cancellation.transactionId,
        'Reject Cancellation',
        'checker',
        checkerComment,
      )
      this.notifyListeners()
    }
  }

  getPendingCancellations(): PendingCancellation[] {
    return this.pendingCancellations.filter((c) => c.status === 'PENDING')
  }

  getAllCancellations(): PendingCancellation[] {
    return [...this.pendingCancellations]
  }

  // --- Manual Settlements ---

  submitSettlement(
    data: Omit<PendingSettlement, 'id' | 'requestedAt' | 'status'>,
  ) {
    const newSettlement: PendingSettlement = {
      ...data,
      id: `SETTLE-${Date.now()}`,
      requestedAt: new Date().toISOString(),
      status: 'PENDING',
    }

    this.pendingSettlements = this.pendingSettlements.filter(
      (s) =>
        !(s.transactionId === data.transactionId && s.status === 'REJECTED'),
    )

    this.pendingSettlements.push(newSettlement)

    if (!this.overrides[data.transactionId]) {
      this.overrides[data.transactionId] = {}
    }
    this.overrides[data.transactionId].settleStatus = 'PENDING'
    this.overrides[data.transactionId].updatedAt = new Date().toISOString()

    this.addActionHistory(
      data.transactionId,
      'Submit Settlement',
      data.requestedBy,
      data.reason,
    )
    this.notifyListeners()
  }

  approveSettlement(settlementId: string, checkerComment: string) {
    const settlement = this.pendingSettlements.find(
      (s) => s.id === settlementId,
    )
    if (settlement) {
      settlement.status = 'APPROVED'
      settlement.checkerComment = checkerComment

      if (!this.overrides[settlement.transactionId]) {
        this.overrides[settlement.transactionId] = {}
      }

      this.overrides[settlement.transactionId].settleStatus = 'APPROVED'
      this.overrides[settlement.transactionId].lifecycleStatus = 'COMPLETED'
      this.overrides[settlement.transactionId].updatedAt =
        new Date().toISOString()

      this.addActionHistory(
        settlement.transactionId,
        'Approve Settlement',
        'checker',
        checkerComment,
      )
      this.notifyListeners()
    }
  }

  rejectSettlement(settlementId: string, checkerComment: string) {
    const settlement = this.pendingSettlements.find(
      (s) => s.id === settlementId,
    )
    if (settlement) {
      settlement.status = 'REJECTED'
      settlement.checkerComment = checkerComment

      if (!this.overrides[settlement.transactionId]) {
        this.overrides[settlement.transactionId] = {}
      }
      this.overrides[settlement.transactionId].settleStatus = 'REJECTED'
      this.overrides[settlement.transactionId].updatedAt =
        new Date().toISOString()

      this.addActionHistory(
        settlement.transactionId,
        'Reject Settlement',
        'checker',
        checkerComment,
      )
      this.notifyListeners()
    }
  }

  getPendingSettlements(): PendingSettlement[] {
    return this.pendingSettlements.filter((s) => s.status === 'PENDING')
  }

  getAllSettlements(): PendingSettlement[] {
    return [...this.pendingSettlements]
  }

  // --- General ---

  getTransactionOverride(
    transactionId: string,
  ): TransactionOverride | undefined {
    return this.overrides[transactionId]
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private notifyListeners() {
    this.listeners.forEach((listener) => listener())
  }
}

export const transactionStore = new TransactionStore()
